import { useState, useEffect } from 'react';
import {
  Settings,
  Plus,
  Save,
  Loader2,
  Trash2,
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminSidebar, AdminMobileHeader } from '@/components/AdminSidebar';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const PAGE_SIZE_OPTIONS = [60, 120, 180, 240, 300, 360, 420, 480, 540, 600];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehiclesPerPage, setVehiclesPerPage] = useState('6');
  const [defaultWhatsapps, setDefaultWhatsapps] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await api.getSettings();
        setSettings(data);
        setVehiclesPerPage(String(data.vehicles_per_page || 6));
        setDefaultWhatsapps(Array.isArray(data.default_whatsapp_numbers) ? data.default_whatsapp_numbers : []);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Error al cargar la configuracion');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.updateSettings({
        vehicles_per_page: parseInt(vehiclesPerPage),
        default_whatsapp_numbers: defaultWhatsapps.filter(wa => wa.number.trim() !== '')
      });
      setSettings(data);
      toast.success('Configuracion guardada correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar la configuracion');
    } finally {
      setSaving(false);
    }
  };

  const handleWhatsappChange = (index, field, value) => {
    const updated = [...defaultWhatsapps];
    updated[index] = { ...updated[index], [field]: value };
    setDefaultWhatsapps(updated);
  };

  const addWhatsapp = () => {
    setDefaultWhatsapps([...defaultWhatsapps, { number: '', label: '' }]);
  };

  const removeWhatsapp = (index) => {
    setDefaultWhatsapps(defaultWhatsapps.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar active="configuracion" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-8">
        <AdminMobileHeader />

        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-wider" data-testid="settings-title">Configuracion</h1>
              <p className="text-sm text-muted-foreground">Ajustes generales del sitio</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Vehicles Per Page */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h2 className="text-lg font-semibold mb-1">Inventario Publico</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Configura como se muestra el inventario en la pagina principal.
                </p>

                <div className="space-y-3">
                  <Label htmlFor="vehicles-per-page" className="text-sm font-medium">
                    Vehiculos por carga inicial
                  </Label>
                  <Select 
                    value={vehiclesPerPage} 
                    onValueChange={setVehiclesPerPage}
                  >
                    <SelectTrigger 
                      className="w-full max-w-xs bg-input border-transparent focus:border-primary"
                      data-testid="vehicles-per-page-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n} vehiculos
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Cantidad de vehiculos que se muestran inicialmente en la pagina de inicio. Los visitantes pueden cargar mas con el boton "Ver Mas".
                  </p>
                </div>
              </div>

              {/* Default WhatsApp Numbers */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-primary" />
                  WhatsApp por Defecto
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Numeros que se asignan automaticamente al crear un nuevo vehiculo. Puedes editarlos en cada vehiculo individualmente.
                </p>

                <div className="space-y-3">
                  {defaultWhatsapps.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">No hay numeros configurados.</p>
                  )}
                  {defaultWhatsapps.map((wa, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input
                        placeholder="33 1234 5678"
                        value={wa.number || ''}
                        onChange={(e) => handleWhatsappChange(index, 'number', e.target.value)}
                        className="bg-input border-transparent focus:border-primary flex-1"
                      />
                      <Input
                        placeholder="Etiqueta (ej: Ventas)"
                        value={wa.label || ''}
                        onChange={(e) => handleWhatsappChange(index, 'label', e.target.value)}
                        className="bg-input border-transparent focus:border-primary flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeWhatsapp(index)}
                        className="text-destructive hover:text-destructive flex-shrink-0"
                        aria-label="Quitar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addWhatsapp}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar numero
                  </Button>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Configuracion
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
